import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { AiMessage } from '@shared/models/ai-chat.model';
import { formatChatMessageTime } from './ai-chat-timestamp.helpers';
import { ChatMarkdown } from './ChatMarkdown';

const useStyles = makeStyles<void, 'hoverTime'>()(
  (_theme, _params, classes) => ({
    row: {
      position: 'relative',
      padding: '12px 18px 12px 20px',
      flexShrink: 0,
      [`&:hover .${classes.hoverTime}`]: { opacity: 1 },
    },
    toolRow: { paddingTop: 6, paddingBottom: 6 },
    text: {
      fontSize: 13,
      lineHeight: '20px',
      opacity: 0.8,
      letterSpacing: '0.52px',
      fontWeight: 400,
      overflowWrap: 'anywhere',
    },
    timestamp: {
      '&.MuiTypography-root': { margin: 0 },
      paddingTop: 14,
      paddingBottom: 2,
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'rgba(0,0,0,0.45)',
      textAlign: 'center',
    },
    hoverTime: {
      position: 'absolute',
      top: 'calc(100% - 8px)',
      left: 20,
      margin: 0,
      opacity: 0,
      pointerEvents: 'none',
      '&.MuiTypography-root': { margin: 0 },
      fontSize: 11,
      lineHeight: '12px',
      fontWeight: 400,
      letterSpacing: '0.2px',
      color: 'rgba(0,0,0,0.45)',
    },
    toolTime: { top: 'calc(100% - 6px)' },
  }),
);

/** Assistant messages, shared by the project agent and FFU import transcript. */
export const AgentChatMessage: React.FC<{
  message: Pick<AiMessage, 'content' | 'createdAt'>;
  timestamp?: string;
  toolLine?: boolean;
  trailing?: React.ReactNode;
}> = ({ message, timestamp, toolLine = false, trailing }) => {
  const { classes, cx } = useStyles();
  const hoverTime = formatChatMessageTime(message.createdAt);
  return (
    <>
      {timestamp && (
        <Typography className={classes.timestamp} component="p">
          {timestamp}
        </Typography>
      )}
      <Box className={cx(classes.row, toolLine && classes.toolRow)}>
        <Typography className={classes.text} component="div">
          <ChatMarkdown content={message.content} trailing={trailing} />
        </Typography>
        {hoverTime && (
          <Typography
            className={cx(classes.hoverTime, toolLine && classes.toolTime)}
            component="p"
          >
            {hoverTime}
          </Typography>
        )}
      </Box>
    </>
  );
};
